<template>
  <Transition name="drawer">
    <div v-if="show" class="fixed inset-x-0 bottom-0 sm:left-64 sm:right-0 z-[110] p-2 sm:p-4 pointer-events-none flex justify-center">
      <div
        class="bg-n-surface-1 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] transition-transform duration-300 pointer-events-auto"
        @click.stop
      >
        <!-- Header Tabs -->
        <div class="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-n-weak bg-n-surface-2">
          <div class="flex bg-n-alpha-1 rounded-xl p-1 overflow-x-auto">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="px-3 sm:px-4 py-1.5 text-xs font-bold rounded-lg transition-all whitespace-nowrap"
              :class="activeTab === tab.id ? 'bg-n-surface-1 text-n-slate-12 shadow-md' : 'text-n-slate-11 hover:text-n-slate-12'"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>
          <div class="flex items-center gap-2">
            <span v-if="isReadOnly" class="px-2 py-0.5 bg-n-alpha-2 text-n-slate-11 text-[10px] font-bold rounded-full uppercase">{{ $t('PIPELINE.EVENT_DRAWER.INVITE_BADGE') }}</span>
            <WootButton v-if="isEditing && !isReadOnly" variant="ghost" size="sm" color="ruby" icon="i-lucide-trash-2" :disabled="isSaving" @click="confirmDelete" />
            <WootButton variant="ghost" size="sm" icon="i-lucide-x" @click="close" />
          </div>
        </div>

        <!-- Form Content -->
        <div class="flex-1 overflow-y-auto p-4 sm:p-8 space-y-5 sm:space-y-6">
          <!-- Title Input -->
          <div class="relative">
            <input
              v-model="form.title"
              type="text"
              :placeholder="$t('PIPELINE.EVENT_DRAWER.TITLE_PLACEHOLDER')"
              class="w-full bg-transparent border-b-2 border-n-weak focus:border-n-brand text-lg sm:text-xl font-bold py-2 outline-none transition-colors placeholder:text-n-slate-9"
              ref="titleInput"
              :disabled="isReadOnly"
              :class="{ 'opacity-80 cursor-not-allowed': isReadOnly }"
            />
          </div>

          <!-- Date/Time Selection -->
          <div class="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-n-slate-12 font-medium bg-n-alpha-1/50 p-4 rounded-2xl">
            <div class="flex items-center gap-2 relative" :class="!isReadOnly ? 'cursor-pointer hover:text-n-brand transition-colors' : 'opacity-80'" v-on-clickaway="() => (showDatePicker = false)">
              <div class="flex h-[42px] items-center gap-2 self-center" @click="!isReadOnly && (showDatePicker = !showDatePicker)">
                <span class="i-lucide-calendar size-4 shrink-0" />
                <span class="leading-none">{{ formattedDate }}</span>
              </div>

              <!-- Custom Date Picker Popover -->
              <div v-if="showDatePicker" class="absolute top-full left-0 mt-2 w-64 bg-n-surface-1 border border-n-weak rounded-xl shadow-xl z-[120] overflow-hidden p-4 pointer-events-auto">
                <div class="flex items-center justify-between mb-4">
                  <span class="text-xs font-bold uppercase tracking-wider">{{ format(pickerMonth, 'MMMM yyyy', { locale: ptBR }) }}</span>
                  <div class="flex gap-1">
                    <WootButton variant="ghost" size="sm" icon="i-lucide-chevron-left" @click="pickerMonth = subMonths(pickerMonth, 1)" />
                    <WootButton variant="ghost" size="sm" icon="i-lucide-chevron-right" @click="pickerMonth = addMonths(pickerMonth, 1)" />
                  </div>
                </div>
                <div class="grid grid-cols-7 gap-1 text-center">
                  <div v-for="d in ['D','S','T','Q','Q','S','S']" :key="d" class="text-[10px] font-bold text-n-slate-9 py-1">{{ d }}</div>
                  <button
                    v-for="day in pickerDays"
                    :key="day.toISOString()"
                    class="size-7 flex items-center justify-center rounded-full text-xs transition-all"
                    :class="[
                      isSameMonth(day, pickerMonth) ? 'text-n-slate-12' : 'text-n-slate-8',
                      isSameDay(day, form.date) ? 'bg-n-brand text-white font-bold' : 'hover:bg-n-alpha-1'
                    ]"
                    @click="selectPickerDate(day)"
                  >
                    {{ day.getDate() }}
                  </button>
                </div>
              </div>
            </div>
            <div class="flex h-[42px] items-center gap-1.5 self-center">
              <div class="flex h-[42px] items-center gap-1">
                <input v-model="form.startTime" class="bg-transparent border-none w-16 h-[42px] leading-none outline-none" :class="isReadOnly ? 'opacity-80 cursor-not-allowed' : 'hover:text-n-brand'" type="time" :disabled="isReadOnly" />
                <span class="i-lucide-arrow-right size-3.5 opacity-50" />
                <input v-model="form.endTime" class="bg-transparent border-none w-16 h-[42px] leading-none outline-none" :class="isReadOnly ? 'opacity-80 cursor-not-allowed' : 'hover:text-n-brand'" type="time" :disabled="isReadOnly" />
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <WootButton v-if="!isReadOnly && !form.hangoutLink && !form.addMeetLink" variant="faded" class="w-full" icon="i-lucide-video" @click="form.addMeetLink = true">
            {{ $t('PIPELINE.EVENT_DRAWER.ADD_VIDEO_CALL') }}
          </WootButton>
          <div v-else-if="form.hangoutLink" class="w-full flex items-center justify-between p-3 bg-n-surface-2 rounded-xl border border-n-weak">
            <div class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
              <span class="i-lucide-video size-4 text-blue-500" />
              <span>{{ $t('PIPELINE.EVENT_DRAWER.GOOGLE_MEET') }}</span>
            </div>
            <a :href="form.hangoutLink" target="_blank" rel="noopener noreferrer" class="text-xs font-bold px-3 py-1.5 bg-n-brand text-white rounded-lg shadow-sm hover:brightness-110 transition-all flex items-center gap-1">
              {{ $t('PIPELINE.EVENT_DRAWER.JOIN') }}
            </a>
          </div>
          <div v-else-if="form.addMeetLink" class="w-full flex items-center justify-between p-3 bg-blue-50/50 rounded-xl border border-blue-100">
            <div class="flex items-center gap-2 text-sm font-medium text-blue-800">
              <span class="i-lucide-video size-4 text-blue-500" />
              <span>{{ $t('PIPELINE.EVENT_DRAWER.VIDEO_CALL_WILL_BE_GENERATED') }}</span>
            </div>
            <button v-if="!isReadOnly" @click="form.addMeetLink = false" class="text-xs font-bold text-n-slate-9 hover:text-n-slate-12">{{ $t('PIPELINE.EVENT_DRAWER.REMOVE') }}</button>
          </div>

          <!-- Contact / Conversation / Deal links -->
          <div class="space-y-3">
            <!-- Contact -->
            <div class="flex items-start gap-4 text-n-slate-11">
              <span class="i-lucide-user size-5 mt-2 shrink-0" />
              <div class="flex-1 border-b border-n-weak pb-3">
                <ComboBox
                  :model-value="form.contact_id"
                  :options="contactOptions"
                  :placeholder="$t('PIPELINE.EVENT_DRAWER.LINK_CONTACT_PLACEHOLDER')"
                  :search-placeholder="$t('PIPELINE.EVENT_DRAWER.LINK_CONTACT_PLACEHOLDER')"
                  :empty-state="$t('PIPELINE.EVENT_DRAWER.NO_RESULTS')"
                  :disabled="isReadOnly"
                  use-api-results
                  class="[&_button]:!bg-n-surface-1 [&_button]:!border-0 [&_button]:!shadow-none [&_button]:!rounded-none [&_button]:!px-0 [&_button]:!outline-none"
                  @update:model-value="onContactSelect"
                  @search="onContactSearch"
                />
              </div>
            </div>

            <!-- Conversation -->
            <div class="flex items-start gap-4 text-n-slate-11">
              <span class="i-lucide-message-square size-5 mt-2 shrink-0" />
              <div class="flex-1 border-b border-n-weak pb-3">
                <span v-if="isReadOnly && !form.conversation_id" class="text-sm text-n-slate-9">-</span>
                <ComboBox
                  v-else
                  :model-value="form.conversation_id"
                  :options="conversationOptions"
                  :placeholder="!form.contact_id ? $t('PIPELINE.EVENT_DRAWER.SELECT_CONTACT_FIRST') : $t('PIPELINE.EVENT_DRAWER.LINK_CONVERSATION_PLACEHOLDER')"
                  :search-placeholder="$t('PIPELINE.EVENT_DRAWER.LINK_CONVERSATION_PLACEHOLDER')"
                  :empty-state="!form.contact_id ? $t('PIPELINE.EVENT_DRAWER.SELECT_CONTACT_FIRST') : $t('PIPELINE.EVENT_DRAWER.NO_RESULTS')"
                  :disabled="isReadOnly || !form.contact_id"
                  use-api-results
                  class="[&_button]:!bg-n-surface-1 [&_button]:!border-0 [&_button]:!shadow-none [&_button]:!rounded-none [&_button]:!px-0 [&_button]:!outline-none"
                  @update:model-value="onConversationSelect"
                  @search="onConversationSearch"
                />
              </div>
            </div>

            <!-- Deal -->
            <div class="flex items-start gap-4 text-n-slate-11">
              <span class="i-lucide-briefcase size-5 mt-2 shrink-0" />
              <div class="flex-1 border-b border-n-weak pb-3">
                <span v-if="isReadOnly && !form.deal_id" class="text-sm text-n-slate-9">-</span>
                <ComboBox
                  v-else
                  :model-value="form.deal_id"
                  :options="dealOptions"
                  :placeholder="!form.contact_id ? $t('PIPELINE.EVENT_DRAWER.SELECT_CONTACT_FIRST') : $t('PIPELINE.EVENT_DRAWER.LINK_DEAL_PLACEHOLDER')"
                  :search-placeholder="$t('PIPELINE.EVENT_DRAWER.LINK_DEAL_PLACEHOLDER')"
                  :empty-state="!form.contact_id ? $t('PIPELINE.EVENT_DRAWER.SELECT_CONTACT_FIRST') : $t('PIPELINE.EVENT_DRAWER.NO_RESULTS')"
                  :disabled="isReadOnly || !form.contact_id"
                  use-api-results
                  class="[&_button]:!bg-n-surface-1 [&_button]:!border-0 [&_button]:!shadow-none [&_button]:!rounded-none [&_button]:!px-0 [&_button]:!outline-none"
                  @update:model-value="onDealSelect"
                  @search="onDealSearch"
                />
              </div>
            </div>
          </div>

          <!-- Participants -->
          <div class="space-y-3">
            <div class="flex items-start gap-4 text-n-slate-11 group transition-colors" :class="!isReadOnly ? 'cursor-pointer hover:text-n-slate-12' : ''">
              <span class="i-lucide-users size-5 mt-0.5" />
              <div class="flex-1 border-b border-n-weak pb-3" :class="!isReadOnly ? 'group-hover:border-n-slate-11' : ''">
                <input
                  v-if="!isReadOnly"
                  v-model="form.attendeesString"
                  type="text"
                  :placeholder="$t('PIPELINE.EVENT_DRAWER.ADD_GUESTS_PLACEHOLDER')"
                  class="w-full bg-transparent border-none outline-none text-sm placeholder:text-n-slate-9"
                />
                <div v-if="form.attendeeList.length > 0" class="mt-2 space-y-1">
                  <div class="text-[10px] font-bold uppercase text-n-slate-9 mb-1">{{ $t('PIPELINE.EVENT_DRAWER.GUESTS_LABEL') }}</div>
                  <div v-for="att in form.attendeeList" :key="att.email" class="flex items-center gap-2">
                    <span class="size-1.5 rounded-full" :class="getAttendeeStatusClass(att.response_status)" />
                    <span class="text-xs truncate text-n-slate-12 font-medium">{{ att.email }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="flex items-start gap-4 text-n-slate-11 group transition-colors" :class="!isReadOnly ? 'cursor-pointer hover:text-n-slate-12' : ''">
              <span class="i-lucide-align-left size-5 mt-0.5" />
              <div class="flex-1 border-b border-n-weak pb-3" :class="!isReadOnly ? 'group-hover:border-n-slate-11' : ''">
                <div class="mb-2 flex items-center justify-end">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 transition-colors"
                    @click="isDescriptionExpanded = true"
                  >
                    <span class="i-lucide-maximize-2 size-3.5" />
                    {{ $t('PIPELINE.EVENT_DRAWER.EXPAND') }}
                  </button>
                </div>
                <textarea
                  v-if="!isReadOnly"
                  v-model="form.description"
                  :placeholder="$t('PIPELINE.EVENT_DRAWER.DESCRIPTION_PLACEHOLDER')"
                  class="w-full bg-n-surface-1 border-none outline-none text-sm resize-none h-20 placeholder:text-n-slate-9"
                />
                <div
                  v-else
                  class="w-full text-sm text-n-slate-12 leading-relaxed whitespace-normal break-words max-h-28 overflow-y-auto"
                  v-html="descriptionPreviewHtml"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="px-4 sm:px-8 py-4 sm:py-6 bg-n-surface-2 border-t border-n-weak flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-center gap-2 sm:gap-4 flex-wrap">
            <!-- Custom Calendar Selector -->
            <div class="relative" v-on-clickaway="() => (showCalendarMenu = false)">
              <div
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-1 rounded-full transition-colors"
                :class="(isEditing || isReadOnly) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-n-alpha-2'"
                @click="!(isEditing || isReadOnly) && (showCalendarMenu = !showCalendarMenu)"
              >
                <span class="i-lucide-calendar-days size-4 text-n-brand" />
                <div class="size-2 rounded-full" :style="{ backgroundColor: calendarColor }" />
              </div>

              <div v-if="showCalendarMenu" class="absolute bottom-full left-0 mb-2 w-48 bg-n-surface-1 border border-n-weak rounded-xl shadow-xl z-[120] overflow-hidden p-1">
                <button
                  v-for="opt in calendarOptions"
                  :key="opt.value"
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-n-alpha-1 transition-colors text-n-slate-12"
                  @click="form.calendarId = opt.value; showCalendarMenu = false"
                >
                  <div class="size-2 rounded-full" :style="{ backgroundColor: getCalendarColor(opt.value) }" />
                  <span class="truncate flex-1 text-left">{{ opt.label }}</span>
                  <span v-if="form.calendarId === opt.value" class="i-lucide-check size-3.5 text-n-brand" />
                </button>
              </div>
            </div>

            <!-- Status selector -->
            <div class="relative" v-on-clickaway="() => (showStatusMenu = false)">
              <div
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors"
                :class="[statusBadgeClass(form.status), !isReadOnly ? 'cursor-pointer hover:brightness-95' : 'opacity-80']"
                @click="!isReadOnly && (showStatusMenu = !showStatusMenu)"
              >
                <span :class="statusIcon(form.status)" class="size-3.5" />
                <span class="text-xs font-bold">{{ statusLabel(form.status) }}</span>
              </div>
              <div v-if="showStatusMenu" class="absolute bottom-full left-0 mb-2 w-44 bg-n-surface-1 border border-n-weak rounded-xl shadow-xl z-[120] overflow-hidden p-1">
                <button
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-n-alpha-1 transition-colors text-n-slate-12"
                  @click="form.status = opt.value; showStatusMenu = false"
                >
                  <span :class="opt.dotClass" class="size-2 rounded-full shrink-0" />
                  <span class="flex-1 text-left text-xs font-medium">{{ opt.label }}</span>
                  <span v-if="form.status === opt.value" class="i-lucide-check size-3.5 text-n-brand" />
                </button>
              </div>
            </div>

            <div @click="!isReadOnly && toggleVisibility()" class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-1 rounded-full transition-colors" :class="!isReadOnly ? 'cursor-pointer hover:bg-n-alpha-2' : 'opacity-80'">
              <span :class="form.isPrivate ? 'i-lucide-lock' : 'i-lucide-globe'" class="size-4" />
              <span class="text-xs font-bold">{{ form.isPrivate ? $t('PIPELINE.EVENT_DRAWER.PRIVATE') : $t('PIPELINE.EVENT_DRAWER.PUBLIC') }}</span>
            </div>

            <!-- Reminder Button -->
            <div class="relative" v-on-clickaway="() => (showReminderPanel = false)">
              <div
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-1 rounded-full transition-colors"
                :class="!isReadOnly ? 'cursor-pointer hover:bg-n-alpha-2' : 'opacity-80'"
                @click="!isReadOnly && (showReminderPanel = !showReminderPanel)"
              >
                <span :class="totalReminders ? 'i-lucide-bell' : 'i-lucide-bell-off'" class="size-4" />
                <span v-if="totalReminders" class="text-xs font-bold">{{ totalReminders }}</span>
              </div>

              <div v-if="showReminderPanel" class="absolute bottom-full left-0 mb-2 w-72 bg-n-surface-1 border border-n-weak rounded-xl shadow-xl z-[120] overflow-hidden p-3 space-y-4">
                <!-- Owner reminders -->
                <div>
                  <div class="text-[10px] font-bold uppercase text-n-slate-9 mb-2 flex items-center gap-1">
                    <span class="i-lucide-user size-3" />
                    {{ $t('PIPELINE.EVENT_DRAWER.REMINDERS_OWNER_LABEL') }}
                  </div>
                  <div class="space-y-1">
                    <div v-for="(min, i) in form.ownerReminders" :key="'owner-'+i" class="flex items-center gap-2">
                      <select
                        :value="min"
                        class="flex-1 bg-n-alpha-1 rounded-lg px-2 py-1 text-xs outline-none"
                        @change="updateOwnerReminder(i, $event.target.value)"
                      >
                        <option v-for="opt in reminderOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                      </select>
                      <button @click="removeOwnerReminder(i)" class="text-n-slate-9 hover:text-red-500">
                        <span class="i-lucide-x size-3.5" />
                      </button>
                    </div>
                  </div>
                  <button @click="addOwnerReminder" class="mt-1.5 text-xs text-n-brand hover:underline flex items-center gap-1">
                    <span class="i-lucide-plus size-3" /> {{ $t('PIPELINE.EVENT_DRAWER.ADD_REMINDER') }}
                  </button>
                </div>

                <!-- Client reminders -->
                <div>
                  <div class="text-[10px] font-bold uppercase text-n-slate-9 mb-2 flex items-center gap-1">
                    <span class="i-lucide-message-square size-3" />
                    {{ $t('PIPELINE.EVENT_DRAWER.REMINDERS_CLIENT_LABEL') }}
                  </div>
                  <div class="space-y-1">
                    <div v-for="(min, i) in form.clientReminders" :key="'client-'+i" class="flex items-center gap-2">
                      <select
                        :value="min"
                        class="flex-1 bg-n-alpha-1 rounded-lg px-2 py-1 text-xs outline-none"
                        @change="updateClientReminder(i, $event.target.value)"
                      >
                        <option v-for="opt in reminderOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                      </select>
                      <button @click="removeClientReminder(i)" class="text-n-slate-9 hover:text-red-500">
                        <span class="i-lucide-x size-3.5" />
                      </button>
                    </div>
                  </div>
                  <button @click="addClientReminder" class="mt-1.5 text-xs text-n-brand hover:underline flex items-center gap-1">
                    <span class="i-lucide-plus size-3" /> {{ $t('PIPELINE.EVENT_DRAWER.ADD_REMINDER') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isReadOnly" class="flex gap-2 w-full sm:w-auto">
            <WootButton variant="ghost" class="flex-1 sm:flex-initial" @click="close">{{ $t('PIPELINE.EVENT_DRAWER.CANCEL') }}</WootButton>
            <WootButton variant="solid" color="blue" class="flex-1 sm:flex-initial" :loading="isSaving" :disabled="isSaving" @click="save">
              {{ isEditing ? $t('PIPELINE.EVENT_DRAWER.SAVE') : $t('PIPELINE.EVENT_DRAWER.CREATE_EVENT') }}
            </WootButton>
          </div>
          <div v-else class="flex gap-2 w-full sm:w-auto">
            <WootButton variant="ghost" class="flex-1 sm:flex-initial" @click="close">{{ $t('PIPELINE.EVENT_DRAWER.CLOSE') }}</WootButton>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <Transition name="drawer">
    <div v-if="show && isDescriptionExpanded" class="fixed inset-x-0 bottom-0 sm:left-64 sm:right-0 z-[130] p-2 sm:p-4 pointer-events-none flex justify-center">
      <div class="bg-n-surface-1 rounded-2xl sm:rounded-3xl shadow-2xl border border-n-weak overflow-hidden flex flex-col w-full max-w-4xl h-[92vh] pointer-events-auto">
        <div class="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-n-weak bg-n-surface-2">
          <span class="text-sm font-semibold text-n-slate-12">{{ $t('PIPELINE.EVENT_DRAWER.DESCRIPTION_PANEL_TITLE') }}</span>
          <WootButton variant="ghost" size="sm" icon="i-lucide-minimize-2" @click="isDescriptionExpanded = false" />
        </div>
        <div class="flex-1 overflow-y-auto p-4 sm:p-6">
          <textarea
            v-if="!isReadOnly"
            v-model="form.description"
            :placeholder="$t('PIPELINE.EVENT_DRAWER.DESCRIPTION_PLACEHOLDER')"
            class="w-full h-full min-h-[60vh] bg-transparent border border-n-weak rounded-xl p-3 outline-none text-sm resize-none placeholder:text-n-slate-9"
          />
          <div
            v-else
            class="w-full h-full text-sm text-n-slate-12 leading-relaxed whitespace-normal break-words"
            v-html="descriptionPreviewHtml"
          />
        </div>
      </div>
    </div>
  </Transition>

  <ConfirmationModal
    ref="confirmDeleteModal"
    :title="$t('PIPELINE.EVENT_DRAWER.DELETE_TITLE')"
    description="Tem certeza que deseja excluir este evento? Esta ação não pode ser desfeita."
    confirm-label="Excluir"
    cancel-label="Cancelar"
  />
</template>

<script>
import WootButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import {
  format,
  addHours,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

export default {
  name: 'EventDrawer',
  components: { WootButton, ComboBox, ConfirmationModal },
  emits: ['change', 'close', 'delete', 'save'],
  props: {
    show: Boolean,
    initialData: {
      type: Object,
      default: () => ({}),
    },
    isSaving: Boolean,
  },
  data() {
    return {
      activeTab: 'event',
      tabs: [
        { id: 'event', label: 'Evento' },
        { id: 'task', label: 'Tarefa' },
      ],
      form: {
        title: '',
        description: '',
        date: new Date(),
        startTime: '10:00',
        endTime: '11:00',
        isPrivate: false,
        calendarId: null,
        ownerReminders: ['10'],
        clientReminders: [],
        status: 'pending',
        addMeetLink: false,
        hangoutLink: null,
        attendeesString: '',
        attendeeList: [],
        contact: null,
        contact_id: null,
        conversation: null,
        conversation_id: null,
        deal: null,
        deal_id: null,
      },
      contactOptions: [],
      conversationOptions: [],
      dealOptions: [],
      showCalendarMenu: false,
      showStatusMenu: false,
      showReminderPanel: false,
      statusOptions: [
        { value: 'pending',     label: 'Pendente',    dotClass: 'bg-n-slate-9' },
        { value: 'confirmed',   label: 'Confirmado',  dotClass: 'bg-green-500' },
        { value: 'rescheduled', label: 'Reagendado',  dotClass: 'bg-yellow-500' },
        { value: 'cancelled',   label: 'Cancelado',   dotClass: 'bg-red-500' },
      ],
      reminderOptions: [
        { label: 'No momento do evento', value: '0' },
        { label: '5 min antes', value: '5' },
        { label: '10 min antes', value: '10' },
        { label: '15 min antes', value: '15' },
        { label: '30 min antes', value: '30' },
        { label: '1 hora antes', value: '60' },
        { label: '2 horas antes', value: '120' },
        { label: '6 horas antes', value: '360' },
        { label: '12 horas antes', value: '720' },
        { label: '1 dia antes', value: '1440' },
        { label: '2 dias antes', value: '2880' },
        { label: '1 semana antes', value: '10080' },
      ],
      showDatePicker: false,
      pickerMonth: new Date(),
      lastEventId: null,
      isDescriptionExpanded: false,
    };
  },
  computed: {
    isEditing() {
      return !!(this.initialData?.id);
    },
    isReadOnly() {
      return !!(this.initialData?.readOnly || this.initialData?.is_organizer === false);
    },
    formattedDate() {
      return format(this.form.date, "eee, d 'de' MMMM", { locale: ptBR });
    },
    calendarColor() {
      // Find calendar in store
      const calendars = this.$store.getters['calendar/getCalendars'];
      const cal = (calendars || []).find(c => c.id === this.form.calendarId);
      return cal?.color || '#3b82f6';
    },
    calendarOptions() {
      const calendars = this.$store.getters['calendar/getCalendars'] || [];
      const editableOnly = calendars.filter(c => c.can_create_events);
      const source = editableOnly.length ? editableOnly : calendars;
      return source.map(c => ({
        label: c.name,
        value: c.id,
      }));
    },
    totalReminders() {
      return (this.form.ownerReminders?.length || 0) + (this.form.clientReminders?.length || 0);
    },
    pickerDays() {
      const start = startOfWeek(startOfMonth(this.pickerMonth));
      const end = endOfWeek(endOfMonth(this.pickerMonth));
      return eachDayOfInterval({ start, end });
    },
    descriptionPreviewHtml() {
      return this.renderDescriptionHtml(this.form.description);
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.resetForm();
        this.fillForm();
      }
    },
    initialData: {
      handler(val) {
        if (this.show && val && val.id !== this.lastEventId) {
          this.fillForm();
        }
      }
    },
    form: {
      handler(newVal) {
        if (this.show) {
          this.$emit('change', { ...newVal });
        }
      },
      deep: true
    }
  },
  methods: {
    format(date, pattern, options) {
      try {
        return format(new Date(date), pattern, options);
      } catch (e) {
        return '';
      }
    },
    getAttendeeStatusClass(responseStatus) {
      if (responseStatus === 'accepted') return 'bg-green-500';
      if (responseStatus === 'declined') return 'bg-red-500';
      return 'bg-n-slate-9';
    },
    fillForm() {
      if (!this.initialData) return;
      this.form.title = this.initialData.title || '';
      this.form.description = this.initialData.description || '';
      this.form.date = new Date(this.initialData.start_at || new Date());
      this.form.startTime = format(this.form.date, 'HH:mm');
      this.form.endTime = format(new Date(this.initialData.end_at || addHours(this.form.date, 1)), 'HH:mm');
      this.form.calendarId = this.initialData.calendar_id;
      this.form.ownerReminders = Array.isArray(this.initialData.owner_reminders) && this.initialData.owner_reminders.length
        ? this.initialData.owner_reminders.map(String)
        : ['10'];
      this.form.clientReminders = Array.isArray(this.initialData.client_reminders)
        ? this.initialData.client_reminders.map(String)
        : [];

      this.form.status = this.initialData.status || 'pending';
      this.form.contact = this.initialData.contact || null;
      this.form.contact_id = this.initialData.contact_id || null;
      this.form.conversation = this.initialData.conversation || null;
      this.form.conversation_id = this.initialData.conversation_id || null;
      this.form.deal = this.initialData.deal || null;
      this.form.deal_id = this.initialData.deal_id || null;
      // Popula options para exibir o label correto no ComboBox ao abrir edição
      if (this.form.contact_id && this.form.contact) {
        const c = this.form.contact;
        this.contactOptions = [{ value: c.id, label: c.name || c.email || `#${c.id}` }];
      }
      if (this.form.conversation_id && this.form.conversation) {
        const conv = this.form.conversation;
        this.conversationOptions = [{ value: conv.id, label: `#${conv.id} — ${conv.meta?.sender?.name || 'Conversa'}` }];
      }
      if (this.form.deal_id && this.form.deal) {
        const d = this.form.deal;
        this.dealOptions = [{ value: d.id, label: `#${d.id} — ${d.title}` }];
      }
      // Pré-carrega conversas e deals do contato (para que a listagem apareça ao abrir o select)
      if (this.form.contact_id) {
        this.loadContactConversations(this.form.contact_id);
        this.loadContactDeals(this.form.contact_id);
      }
      this.form.hangoutLink = this.initialData.metadata?.hangout_link || null;
      this.form.addMeetLink = !this.form.hangoutLink && !!this.initialData.metadata?.add_meet_link;
      this.form.attendeeList = (this.initialData.metadata?.attendees || [])
        .map(att => (typeof att === 'string' ? { email: att, response_status: 'needsAction' } : att))
        .filter(att => att?.email);
      this.form.attendeesString = this.form.attendeeList.map(a => a.email).join(', ');

      this.showCalendarMenu = false;
      this.showNotificationMenu = false;
      this.showDatePicker = false;
      this.pickerMonth = this.form.date;

      if (this.initialData?.id !== this.lastEventId) {
        this.lastEventId = this.initialData?.id;
        this.$nextTick(() => {
          this.$refs.titleInput?.focus();
        });
      }
    },
    resetForm() {
      const calendars = this.$store.getters['calendar/getCalendars'] || [];
      const editableOnly = calendars.filter(c => c.can_create_events);
      const defaultCalendar = editableOnly[0] || calendars[0];
      this.form = {
        title: '',
        description: '',
        date: new Date(),
        startTime: '10:00',
        endTime: '11:00',
        isPrivate: false,
        calendarId: defaultCalendar?.id,
        ownerReminders: ['10'],
        clientReminders: [],
        status: 'pending',
        addMeetLink: false,
        hangoutLink: null,
        attendeesString: '',
        attendeeList: [],
        contact: null,
        contact_id: null,
        conversation: null,
        conversation_id: null,
        deal: null,
        deal_id: null,
      };
      this.contactOptions = [];
      this.conversationOptions = [];
      this.dealOptions = [];
      this.showCalendarMenu = false;
      this.showNotificationMenu = false;
      this.isDescriptionExpanded = false;
      this.allContactConversations = [];
      this.allContactDeals = [];
    },
    getCalendarColor(id) {
      const calendars = this.$store.getters['calendar/getCalendars'];
      const cal = (calendars || []).find(c => c.id === id);
      return cal?.color || '#3b82f6';
    },
    statusLabel(status) {
      const map = { pending: 'Pendente', confirmed: 'Confirmado', rescheduled: 'Reagendado', cancelled: 'Cancelado' };
      return map[status] || 'Pendente';
    },
    statusIcon(status) {
      const map = { pending: 'i-lucide-clock', confirmed: 'i-lucide-check-circle', rescheduled: 'i-lucide-refresh-cw', cancelled: 'i-lucide-x-circle' };
      return map[status] || 'i-lucide-clock';
    },
    statusBadgeClass(status) {
      const map = {
        pending:     'bg-n-alpha-1 text-n-slate-11',
        confirmed:   'bg-green-100 text-green-800',
        rescheduled: 'bg-yellow-100 text-yellow-800',
        cancelled:   'bg-red-100 text-red-700',
      };
      return map[status] || 'bg-n-alpha-1 text-n-slate-11';
    },
    addOwnerReminder() {
      this.form.ownerReminders = [...this.form.ownerReminders, '10'];
    },
    updateOwnerReminder(index, value) {
      const updated = [...this.form.ownerReminders];
      updated[index] = value;
      this.form.ownerReminders = updated;
    },
    removeOwnerReminder(index) {
      this.form.ownerReminders = this.form.ownerReminders.filter((_, i) => i !== index);
    },
    addClientReminder() {
      this.form.clientReminders = [...this.form.clientReminders, '60'];
    },
    updateClientReminder(index, value) {
      const updated = [...this.form.clientReminders];
      updated[index] = value;
      this.form.clientReminders = updated;
    },
    removeClientReminder(index) {
      this.form.clientReminders = this.form.clientReminders.filter((_, i) => i !== index);
    },
    onDateChange(event) {
      const newDate = parseISO(event.target.value);
      if (Number.isNaN(newDate.getTime())) return;
      this.form.date = newDate;
    },
    selectPickerDate(day) {
      this.form.date = day;
      this.showDatePicker = false;
    },
    isSameDay(d1, d2) {
      return isSameDay(d1, d2);
    },
    isSameMonth(d1, d2) {
      return isSameMonth(d1, d2);
    },
    addMonths(d, n) {
      return addMonths(d, n);
    },
    subMonths(d, n) {
      return subMonths(d, n);
    },
    close() {
      this.isDescriptionExpanded = false;
      this.$emit('close');
    },
    toggleVisibility() {
      this.form.isPrivate = !this.form.isPrivate;
    },
    async confirmDelete() {
      const confirmed = await this.$refs.confirmDeleteModal.showConfirmation();
      if (confirmed) {
        this.$emit('delete', this.initialData.id);
      }
    },
    save() {
      if (this.isReadOnly) return;
      if (!this.form.title) {
        // eslint-disable-next-line no-console
        console.warn('O título é obrigatório');
        return;
      }

      // Reconstruct dates
      const start = new Date(this.form.date);
      const [sh, sm] = this.form.startTime.split(':');
      start.setHours(sh, sm, 0, 0);

      const end = new Date(this.form.date);
      const [eh, em] = this.form.endTime.split(':');
      end.setHours(eh, em, 0, 0);

      const attendees = this.form.attendeesString
        ? this.form.attendeesString.split(',').map(e => e.trim()).filter(e => e)
        : [];

      this.$emit('save', {
        ...this.form,
        id: this.initialData?.id,
        calendar_id: this.form.calendarId,
        contact_id: this.form.contact_id || null,
        conversation_id: this.form.conversation_id || null,
        deal_id: this.form.deal_id || null,
        status: this.form.status,
        owner_reminders: this.form.ownerReminders,
        client_reminders: this.form.clientReminders,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        metadata: {
          attendees: attendees.length > 0 ? attendees : undefined,
          add_meet_link: this.form.addMeetLink
        }
      });
    },
    // Contact search — chamado pelo evento @search do ComboBox
    async onContactSearch(query) {
      if (!query || query.length < 2) return;
      const { accountId } = this.$store.state.route.params;
      const res = await window.axios.get(`/api/v1/accounts/${accountId}/contacts/search`, {
        params: { q: query, include_contacts: true }
      });
      this.contactOptions = (res.data?.payload || []).slice(0, 10).map(c => ({
        value: c.id,
        label: c.name ? `${c.name}${c.email ? ' — ' + c.email : ''}` : (c.email || `#${c.id}`),
      }));
      // garante que o item selecionado está sempre na lista
      if (this.form.contact_id && !this.contactOptions.find(o => o.value === this.form.contact_id)) {
        const c = this.form.contact;
        if (c) this.contactOptions.unshift({ value: c.id, label: c.name || c.email || `#${c.id}` });
      }
    },
    async onContactSelect(id) {
      const opt = this.contactOptions.find(o => o.value === id);
      this.form.contact_id = id || null;
      this.form.contact = id && opt ? { id, name: opt.label } : null;
      // Limpa vínculos dependentes ao trocar contato
      this.form.conversation_id = null;
      this.form.conversation = null;
      this.form.deal_id = null;
      this.form.deal = null;
      this.allContactConversations = [];
      this.allContactDeals = [];
      this.conversationOptions = [];
      this.dealOptions = [];
      if (id) {
        await this.loadContactConversations(id);
        await this.loadContactDeals(id);
      }
    },
    async loadContactConversations(contactId) {
      const { accountId } = this.$store.state.route.params;
      try {
        const res = await window.axios.get(
          `/api/v1/accounts/${accountId}/contacts/${contactId}/conversations`
        );
        const all = res.data?.payload || [];
        // Mostra apenas conversas abertas (status: open)
        this.allContactConversations = all.filter(c => c.status === 'open');
        this.conversationOptions = this.allContactConversations.slice(0, 20).map(c => ({
          value: c.id,
          label: `#${c.id} — ${c.meta?.sender?.name || c.meta?.sender?.identifier || 'Conversa'}`,
        }));
      } catch (_) {
        this.allContactConversations = [];
      }
    },
    async loadContactDeals(contactId) {
      const { accountId } = this.$store.state.route.params;
      try {
        const res = await window.axios.get(`/api/v1/accounts/${accountId}/deals`, {
          params: { contact_id: contactId, status: 'open' }
        });
        const all = res.data?.data || res.data || [];
        this.allContactDeals = all;
        this.dealOptions = this.allContactDeals.slice(0, 20).map(d => ({
          value: d.id,
          label: `#${d.id} — ${d.title}`,
        }));
      } catch (_) {
        this.allContactDeals = [];
      }
    },
    // Conversation search — filtra dentro das conversas já carregadas do contato
    onConversationSearch(query) {
      if (!this.form.contact_id) return;
      const all = this.allContactConversations || [];
      if (!query) {
        this.conversationOptions = all.slice(0, 20).map(c => ({
          value: c.id,
          label: `#${c.id} — ${c.meta?.sender?.name || c.meta?.sender?.identifier || 'Conversa'}`,
        }));
        return;
      }
      const q = query.replace(/^#/, '').toLowerCase();
      this.conversationOptions = all
        .filter(c => String(c.id).includes(q) || (c.meta?.sender?.name || '').toLowerCase().includes(q))
        .slice(0, 20)
        .map(c => ({
          value: c.id,
          label: `#${c.id} — ${c.meta?.sender?.name || c.meta?.sender?.identifier || 'Conversa'}`,
        }));
    },
    onConversationSelect(id) {
      const opt = this.conversationOptions.find(o => o.value === id);
      this.form.conversation_id = id || null;
      this.form.conversation = id && opt ? { id, meta: { sender: { name: opt.label } } } : null;
    },
    // Deal search — filtra dentro dos deals já carregados do contato
    onDealSearch(query) {
      if (!this.form.contact_id) return;
      const all = this.allContactDeals || [];
      if (!query) {
        this.dealOptions = all.slice(0, 20).map(d => ({ value: d.id, label: `#${d.id} — ${d.title}` }));
        return;
      }
      const q = query.replace(/^#/, '').toLowerCase();
      this.dealOptions = all
        .filter(d => String(d.id).includes(q) || (d.title || '').toLowerCase().includes(q))
        .slice(0, 20)
        .map(d => ({ value: d.id, label: `#${d.id} — ${d.title}` }));
    },
    onDealSelect(id) {
      const opt = this.dealOptions.find(o => o.value === id);
      this.form.deal_id = id || null;
      this.form.deal = id && opt ? { id, name: opt.label } : null;
    },
    renderDescriptionHtml(raw) {
      if (!raw) return '<span class="text-n-slate-9">Sem descrição</span>';

      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      const allowedTags = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'UL', 'OL', 'LI']);

      const sanitizeNode = node => {
        [...node.children].forEach(child => {
          if (!allowedTags.has(child.tagName)) {
            const textNode = doc.createTextNode(child.textContent || '');
            child.replaceWith(textNode);
            return;
          }
          [...child.attributes].forEach(attr => child.removeAttribute(attr.name));
          sanitizeNode(child);
        });
      };

      sanitizeNode(root);
      return root.innerHTML.replace(/\n/g, '<br>');
    },
  },
};
</script>

<style scoped>
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-active div,
.drawer-leave-active div {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-enter-from div {
  transform: translateY(100%);
}

.drawer-leave-to div {
  transform: translateY(100%);
}
</style>
